import { Flex, IconButton, spacing } from '@lemonade-hq/uniclient-theme';
import React, { useCallback, useEffect, useRef } from 'react';
import * as styles from './FreeTextInput.css';
import { MAX_HEIGHT, MIN_HEIGHT } from './FreeTextInput.css';

const MAX_CHARACTERS = 500;

export interface FreeTextInputProps {
  readonly value: string;
  readonly onSubmit: () => Promise<void>;
  readonly onChange: (newValue: string) => Promise<void>;
  readonly isLoading: boolean;
  readonly placeholder?: string;
}

export const FreeTextInput: React.FC<FreeTextInputProps> = ({
  value,
  onSubmit,
  onChange,
  isLoading,
  placeholder = 'Ask anything...',
}) => {
  const inputRef = useRef<HTMLDivElement>(null);

  const autoResize = useCallback(() => {
    if (inputRef.current) {
      const element = inputRef.current;
      element.style.height = 'auto';
      const scrollHeight = element.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, MIN_HEIGHT), MAX_HEIGHT);
      element.style.height = `${newHeight}px`;
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (isLoading || !value) return;

    void onSubmit();
  }, [value, onSubmit, isLoading]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isLoading) {
        e.preventDefault();
        return;
      }

      // Prevent regular character input when at limit
      if (value.length >= MAX_CHARACTERS && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        return;
      }

      if (e.key === 'Enter') {
        if (e.shiftKey) {
          if (value.length >= MAX_CHARACTERS) {
            e.preventDefault();
          }

          return;
        }

        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit, isLoading, value],
  );

  const handleOnChange = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      if (isLoading) return;

      const target = e.currentTarget;
      const text = target.textContent ?? '';
      const trimmedText = text.trim();

      // If the text is empty after trimming, but contains newlines, preserve them
      // Otherwise, clear the div to ensure CSS :empty works
      if (trimmedText === '') {
        if (text.includes('\n')) {
          // Preserve newlines even when no other content
          void onChange(text);
        } else {
          target.textContent = '';
          target.innerHTML = '';
          void onChange('');
        }
      } else {
        if (text.length > MAX_CHARACTERS) {
          // Truncate to the limit for paste operations
          const truncatedText = text.slice(0, MAX_CHARACTERS);
          target.textContent = truncatedText;
          void onChange(truncatedText);
          return;
        }

        void onChange(text);
      }

      // Trigger auto-resize after content change
      autoResize();
    },
    [onChange, isLoading, autoResize],
  );

  // Sync external value changes with the contenteditable div
  useEffect(() => {
    if (inputRef.current) {
      const currentContent = inputRef.current.textContent ?? '';
      const newValue = value || '';

      if (currentContent !== newValue) {
        if (!newValue) {
          // For empty values, clear the content and ensure it's properly empty
          inputRef.current.textContent = '';
          inputRef.current.innerHTML = '';
        } else {
          inputRef.current.textContent = newValue;
        }
      }
    }
  }, [value]);

  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  const canSubmit = value.trim().length > 0 && !isLoading;

  return (
    <Flex className={styles.container} flexDirection="column" gap={spacing.s12}>
      <Flex alignItems="flex-end" flexDirection="row" gap={spacing.s16}>
        <Flex flex={1} position="relative">
          <div className={styles.textAreaWrapper}>
            <div
              aria-invalid="false"
              aria-label={placeholder}
              aria-multiline="true"
              aria-required="false"
              className={styles.textAreaEditor({ loading: isLoading })}
              contentEditable={isLoading ? 'false' : 'plaintext-only'}
              data-placeholder={placeholder}
              onInput={handleOnChange}
              onKeyDown={handleKeyDown}
              ref={inputRef}
              role="textbox"
              tabIndex={isLoading ? -1 : 0}
            />
          </div>
        </Flex>

        <IconButton className={styles.sendButton} disabled={!canSubmit} icon="send" onClick={handleSubmit} size="md" />
      </Flex>
    </Flex>
  );
};
